import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { MenuItem, SecondLevelListProps } from "../types";
import { UserBlockProfileMenu } from "../UserBlockProfileMenu/UserBlockProfileMenu";
import { UserBlockCompanyMenu } from "../UserBlockCompanyMenu/UserBlockCompanyMenu";
import { useAppState } from "Context/StateContext";
import Leave from "Components/Generic/Modal/Leave";
import LoadingScreen from "Components/LoadingScreen";
import { Company } from "Models/company";
import { User } from "Models/user";
import useCurrentBrand from "Hooks/useCurrentBrand";
import UserService from "Services/user.service";
import { Resources } from "Models/resources";
import { ResourceGroup } from "Util/enums";
import { getBrandCustom } from "Util/getCustomColor";
import {
  SELECTED_COMPANIES_IDS,
  SELECTED_TASK_TYPES,
  USER_COMPANY,
  USER_TOKEN,
  USER_UID,
} from "Constants";
import { getBrandByCompanyName } from "Util/brands";
import SvgIconUrl from "../../../helpers/SvgIcon/SvgIconUrl";

const BACKICON = require("Assets/icons/arrow-back-2.svg");
const NEXTICON = require("Assets/icons/arrow-right-3.svg");

type Props = SecondLevelListProps & {
  onRequestClose?: () => void;
};

const SecondLevelList: React.FC<Props> = ({
  openNode,
  left,
  onClose,
  onOpenThird,
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const {
    currentUser,
    setCurrentUser,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setHasUserChanged,
  } = useAppState();
  const { setCurrentBrand } = useCurrentBrand();
  const history = useHistory();

  const [userToSwitch, setUserToSwitch] = useState<User>();
  const [billingIsAllowed, setBillingIsAllowed] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const listRef = useRef<HTMLUListElement | null>(null);

  const navigateSmart = (path?: string, state?: any) => {
    if (!path) return;
    onClose?.();
    onRequestClose?.();
    if (history.location.pathname === path) {
      history.replace(path, state);
    } else {
      history.push(path, state);
    }
  };

  const avatarUrl = useMemo(
    () => currentUser?.picture?.mediaLink ?? require("Assets/profile.png"),
    [currentUser]
  );

  const { mutate: switchCompany } = useMutation(
    ["user.company.switch"],
    (user: { id: number; companyId: number }) => UserService.switchCompany(user.id, user.companyId),
    {
      onSuccess: (response) => {
        if (response.data) {
          const { id } = response.data.user.company;

          setCurrentBrand(getBrandByCompanyName(id!));

          localStorage.setItem(USER_COMPANY, JSON.stringify(response.data.user.company));
          localStorage.setItem(USER_TOKEN, response.data.token);
          localStorage.setItem(USER_UID, response.data.token);
          localStorage.setItem(
            SELECTED_COMPANIES_IDS,
            JSON.stringify([response.data.user.company.id])
          );
          localStorage.removeItem(SELECTED_TASK_TYPES);
          setCurrentUser(response.data.user);
        }
        setUserToSwitch(undefined);
        setHasUserChanged(true);
        location.reload();
        location.replace("/");
      },
    }
  );

  useEffect(() => {
    let isAllowed = false;
    if (currentUser?.resources) {
      const billingRes: Resources | undefined = currentUser.resources.find(
        (res) =>
          res?.group?.toLowerCase() === ResourceGroup.BILLING.toLowerCase() &&
          res?.roles_resources?.view
      );
      if (billingRes) isAllowed = true;
    }
    setBillingIsAllowed(isAllowed);
  }, [currentUser]);

  useEffect(() => {
    const container = listRef.current;
    if (!container) return;

    const selectedEl =
      (container.querySelector('button.Mui-selected[data-level="second"]') as HTMLElement | null) ||
      (container.querySelector("button.Mui-selected") as HTMLElement | null);

    if (selectedEl) {
      try {
        selectedEl.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      } catch {
        selectedEl.scrollIntoView?.({ block: "nearest", inline: "nearest" } as any);
      }
    }
  }, [activeKey, openNode]);

  const selectedPill = {
    backgroundColor: getBrandCustom("headerBackgroundColor", "#F1F6FB"),
    color: getBrandCustom("primaryColor", "#0055A4"),
  } as const;
  const hoverPill = {
    backgroundColor: getBrandCustom("headerBackgroundColor", "#F2F4F7"),
    color: getBrandCustom("primaryColor", "#1D2939"),
  } as const;

  const renderNode = (node: MenuItem): React.ReactNode => {
    const isNodeActive = activeKey === node.key;

    if (node.ui === "third-drawer") {
      return (
        <ListItemButton
          key={node.key}
          selected={isNodeActive}
          onClick={(e) => {
            e.stopPropagation();
            setActiveKey(node.key);
            onOpenThird(node.key);
          }}
          sx={{
            borderRadius: 1,
            mx: 1,
            fontFamily: "Manrope",
            fontSize: 15,
            fontWeight: 500,
            letterSpacing: 0.75,
            color: isNodeActive ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
            "&.Mui-selected": selectedPill,
            ...(isNodeActive ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
          }}
        >
          {node.icon && (
            <ListItemIcon sx={{ minWidth: 17, mr: 1.5, alignItems: "center", color: "inherit" }}>
              <SvgIconUrl src={node.icon} size={17} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={t(node.label) ?? node.label ?? ""}
            primaryTypographyProps={{ noWrap: true, title: (t(node.label) ?? node.label ?? "") as string }}
          />
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", color: "inherit" }}>
            <SvgIconUrl src={NEXTICON} size={21} />
          </Box>
        </ListItemButton>
      );
    }

    if (node.children?.length) {
      const expanded = isNodeActive || node.children.some((c) => c.key === activeKey);
      const parentActive = isNodeActive;

      return (
        <Accordion
          key={node.key}
          disableGutters
          elevation={0}
          expanded={expanded}
          onChange={(_, open) => {
            setActiveKey(open ? node.key : null);
            onOpenThird(null);
          }}
          sx={{ mx: 0, my: 0.5, borderRadius: 2, "&::before": { display: "none" } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              mx: 1,
              borderRadius: 1,
              color: parentActive ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
              ...(parentActive ? selectedPill : {}),
              ...(parentActive ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
              "& .MuiAccordionSummary-expandIconWrapper": {
                color: parentActive ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
                transition: "color .2s ease",
              },
            }}
          >
            {node.icon && (
              <ListItemIcon sx={{ minWidth: 17, mr: 1.5, alignItems: "center", color: "inherit" }}>
                <SvgIconUrl src={node.icon} size={17} />
              </ListItemIcon>
            )}
            <ListItemText
              primary={t(node.label) ?? node.label ?? ""}
              primaryTypographyProps={{ noWrap: true, title: (t(node.label) ?? node.label ?? "") as string }}
            />
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0 }}>
            <List sx={{ py: 0 }}>
              {node.children.map((c) => {
                const isActiveChild = activeKey === c.key;

                if (c.ui === "third-drawer") {
                  return (
                    <ListItemButton
                      key={c.key}
                      selected={isActiveChild}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveKey(c.key);
                        onOpenThird(c.key);
                      }}
                      sx={{
                        pl: 6,
                        borderRadius: 1,
                        mx: 1,
                        color: isActiveChild
                          ? getBrandCustom("primaryColor", "#0055A4")
                          : "#475467",
                        "&.Mui-selected": selectedPill,
                        ...(isActiveChild ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
                      }}
                    >
                      {c.icon && (
                        <ListItemIcon
                          sx={{ minWidth: 17, mr: 1.5, alignItems: "center", color: "inherit" }}
                        >
                          <SvgIconUrl src={c.icon} size={17} />
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={t(c.label) ?? c.label ?? ""}
                        primaryTypographyProps={{ noWrap: true, title: (t(c.label) ?? c.label ?? "") as string }}
                      />
                      <Box
                        sx={{ ml: "auto", display: "flex", alignItems: "center", color: "inherit" }}
                      >
                        <SvgIconUrl src={NEXTICON} size={21} />
                      </Box>
                    </ListItemButton>
                  );
                }

                return (
                  <ListItemButton
                    key={c.key}
                    selected={isActiveChild}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveKey(c.key);
                      onOpenThird(null);
                      navigateSmart(c.path, c.state);
                    }}
                    sx={{
                      pl: 6,
                      borderRadius: 1,
                      mx: 1,
                      fontFamily: "Manrope",
                      fontWeight: 600,
                      fontSize: 15,
                      color: isActiveChild ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
                      "&.Mui-selected": selectedPill,
                      ...(isActiveChild ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
                    }}
                  >
                    {c.icon && (
                      <ListItemIcon
                        sx={{ minWidth: 17, mr: 1.5, alignItems: "center", color: "inherit" }}
                      >
                        <SvgIconUrl src={c.icon} size={17} />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={t(c.label) ?? c.label ?? ""}
                      primaryTypographyProps={{ noWrap: true, title: (t(c.label) ?? c.label ?? "") as string }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (
      <ListItemButton
        key={node.key}
        selected={isNodeActive}
        onClick={() => {
          setActiveKey(node.key);
          onOpenThird(null);
          navigateSmart(node.path, node.state);
        }}
        sx={{
          borderRadius: 1,
          mx: 1,
          fontFamily: "Manrope",
          fontWeight: 600,
          fontSize: 15,
          color: isNodeActive ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
          "&.Mui-selected": selectedPill,
          ...(isNodeActive ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
        }}
      >
        {node.icon && (
          <ListItemIcon sx={{ minWidth: 17, mr: 1.5, alignItems: "center", color: "inherit" }}>
            <SvgIconUrl src={node.icon} size={17} />
          </ListItemIcon>
        )}
        <ListItemText
          primary={t(node.label) ?? node.label ?? ""}
          primaryTypographyProps={{ noWrap: true, title: (t(node.label) ?? node.label ?? "") as string }}
        />
      </ListItemButton>
    );
  };

  const handleBack = () => {
    setActiveKey(null);
    onClose();
  };

  let content: React.ReactNode = null;

  if (!openNode) {
    content = null;
  } else if (openNode.key === "profile") {
    content = (
      <Box sx={{ p: 2 }}>
        <UserBlockProfileMenu
          billingIsAllowed={billingIsAllowed}
          handleCloseProfileMenu={onClose}
          avatarUrl={avatarUrl}
        />
      </Box>
    );
  } else if (openNode.key === "company") {
    content = (
      <>
        <LoadingScreen showing={isSwitching} message={t("switchingAccounts")} />
        <Leave
          open={!!userToSwitch}
          onClose={(ok) => {
            if (ok) {
              setHasUnsavedChanges(false);
              if (userToSwitch?.id && userToSwitch?.companyId) {
                switchCompany({ id: currentUser!.id, companyId: userToSwitch.companyId });
                setIsSwitching(true);
              }
              return;
            }
            setUserToSwitch(undefined);
          }}
        />
        <Box sx={{ p: 2, pb: 0, display: "flex", flexDirection: "column", height: "100%" }}>
          <UserBlockCompanyMenu
            loadingSwitchCompany={isSwitching}
            handleCloseCompanyMenu={onClose}
            handleSwitchCompany={(company: Company) => {
              if (!currentUser?.id || !company.id) return;
              if (hasUnsavedChanges) {
                setUserToSwitch({ id: currentUser.id, companyId: company.id } as User);
                return;
              }
              switchCompany({ id: currentUser.id, companyId: company.id });
              setIsSwitching(true);
            }}
          />
        </Box>
      </>
    );
  } else {
    content = (
      <Box sx={{ p: 2 }}>
        {(openNode.sectionTitle || openNode.label) && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ListItemIcon
              sx={{
                color: getBrandCustom("primaryColor", "#0055A4"),
                cursor: "pointer",
                minWidth: 17,
                mr: "14px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleBack();
              }}
            >
              <SvgIconUrl src={BACKICON} size={22} />
            </ListItemIcon>
            <ListItemText
              primary={t(openNode.sectionTitle ?? openNode.label ?? "")}
              primaryTypographyProps={{
                noWrap: true,
                title: (t(openNode.sectionTitle ?? openNode.label ?? "") as string),
                fontSize: 16,
                fontWeight: 500,
                letterSpacing: 0.64,
                textTransform: "uppercase",
                color: getBrandCustom("primaryColor", "#0055A4"),
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            mt: 1,
            maxHeight: "calc(100vh - 120px)",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <List ref={listRef} sx={{ py: 0 }}>
            {openNode.children?.map((child) => renderNode(child))}
          </List>
        </Box>
      </Box>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={!!openNode}
      hideBackdrop
      variant="persistent"
      PaperProps={{
        sx: {
          width: "fit-content",
          left,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid #E5E7EB",
          boxShadow: "none",
          zIndex: 1101,
        },
        "data-sidebar": "second",
      }}
    >
      {content}
    </Drawer>
  );
};

export default SecondLevelList;
